// Chapter 21 - Imitation, Modeling, and Observational Learning
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 21.

import type { ChapterModule } from "./types";

const chapter21Module: ChapterModule = {
  meta: {
    id: "21",
    number: 21,
    title: "Imitation, Modeling, and Observational Learning",
    subtitle: "Generalized imitation, video modeling",
    part: "Part 8 - Developing New Behavior",
    estMinutes: 65,
    bacb: ["G-5", "G-6"],
  },
  curated: true,
  overview: [
    "Chapter 21 distinguishes three related but distinct phenomena: IMITATION (a tightly defined behavior class with four criteria), MODELING (a behavior change strategy that uses demonstration), and OBSERVATIONAL LEARNING (using another person's behavior AND its consequences to decide whether to imitate). Imitation training builds the prerequisite repertoire; video modeling, video self-modeling, and the use of live models extend it.",
    "You will learn the four imitation criteria, generalized imitation, Striefel's (1974) imitation-training protocol, factors that enhance modeling effectiveness, and the three observational learning prerequisites (attending, imitating, discriminating).",
  ],
  objectives: [
    { id: "G-5", text: "Use modeling and imitation training to teach skills." },
    { id: "G-6", text: "Plan and conduct video modeling and observational learning programs." },
  ],
  coreConcepts: [
    {
      title: "Four criteria for imitation",
      body: [
        "True imitation requires: (a) the imitative behavior is OCCASIONED by another person's model, (b) the imitative behavior has FORMAL SIMILARITY with the model, (c) the imitative behavior follows the model CLOSELY IN TIME, and (d) the MODEL is the primary controlling variable.",
      ],
      bullets: [
        "A model is a live or symbolic demonstration that shows the learner exactly what to do.",
        "Formal similarity: the imitator's behavior physically resembles the model and is in the same mode.",
        "Brief latency is an important feature; most 'delayed imitation' is better explained by other principles.",
      ],
    },
    {
      title: "Imitation training and generalized imitation",
      body: [
        "Imitation training is a systematic, research-based set of steps for teaching a non-imitative learner to imitate models of novel behaviors. Generalized imitation refers to a learner imitating a wide variety of UNPROMPTED, UNTRAINED, NON-REINFORCED modeled behaviors across settings.",
      ],
    },
    {
      title: "Striefel's (1974) imitation training protocol",
      body: [
        "(a) Assess and teach prerequisite skills for imitation training; (b) select models for training; (c) pretest; (d) sequence models for training; (e) conduct imitation training. Guidelines include keeping sessions brief and active, reinforcing both prompted and imitative responses, pairing praise with tangibles, backing up when progress stalls, fading prompts, measuring and reviewing data, and setting termination criteria.",
      ],
    },
    {
      title: "Modeling",
      body: [
        "Modeling uses a live or symbolic model to teach new skills. Video modeling presents the target behavior on video. Video SELF-modeling shows the learner themselves performing the target successfully and instructs them to imitate.",
      ],
    },
    {
      title: "Factors that enhance modeling effectiveness",
      body: [
        "Models similar to the learner; models with social status/prestige; emphasis on critical aspects of the target; instructions to attend to the model; authentic context; rehearsal and feedback; learner observing the model receive reinforcement for the target; learner receiving reinforcement for imitating.",
      ],
    },
    {
      title: "Observational learning",
      body: [
        "Observational learning involves observing another person's BEHAVIOR AND ITS CONSEQUENCES, then using that information to decide whether to imitate. It typically involves imitation but does not require it. Three prerequisite skills: ATTENDING, IMITATING, and DISCRIMINATING. If any skill is lacking, it can be taught separately and then combined.",
      ],
    },
  ],
  glossary: [
    { term: "Imitation", definition: "Behavior occasioned by another person's model, with formal similarity, close temporal proximity, and the model as the primary controlling variable.", sourceHint: "Imitation entails four criteria: (a) The imitative behavior is occasioned by another person's model... (b) the imitative behavior has formal similarity with the model, (c) the imitative behavior follows the modeled behavior closely in time, and (d) the model is the primary controlling variable for the imitative behavior." },
    { term: "Model", definition: "A live or symbolic demonstration of the target behavior that shows the learner what to do.", sourceHint: "A model is a live or symbolic demonstration of the target behavior that shows the learner exactly what to do." },
    { term: "Generalized imitation", definition: "Learner imitates a wide variety of unprompted, untrained, non-reinforced modeled behaviors across settings.", sourceHint: "Generalized imitation refers to a learner imitating a wide variety of unprompted, untrained, non-reinforced modeled behaviors in different settings and situations." },
    { term: "Imitation training", definition: "Systematic, research-based set of steps for teaching non-imitative learners to imitate novel models.", sourceHint: "Imitation training is a systematic, research-based set of steps for teaching a non-imitative learner to imitate models of novel behaviors." },
    { term: "Modeling", definition: "Behavior change strategy in which learners acquire skills by imitating demonstrations of the skill.", sourceHint: "Modeling is a behavior change strategy in which learners acquire new skills by imitating demonstrations of the skills by live or symbolic models." },
    { term: "Video modeling", definition: "Modeling technique that uses video demonstrations of the target behavior.", sourceHint: "Video modeling is a technique that involves the demonstration of desired behaviors through visual representations of the behavior." },
    { term: "Video self-modeling", definition: "Modeling technique in which the learner observes a video of themselves successfully performing the target.", sourceHint: "In video self-modeling, the learners observe a video of themselves successfully performing the target behavior and imitates their self-model." },
    { term: "Observational learning", definition: "Learning that involves observing another person's behavior AND its consequences and using that information to decide whether to imitate.", sourceHint: "Observational learning entails another person's behavior and its consequence(s) and using that information to determined whether or not to imitate the behavior." },
  ],
  procedures: [
    {
      title: "Run an imitation-training program (Striefel protocol)",
      intro: "Build a generalized imitative repertoire from scratch.",
      steps: [
        { title: "Assess prerequisites", detail: "Eye contact, attending, basic motor responses; teach if missing." },
        { title: "Select models", detail: "Start with gross motor models that the learner can produce; progress to finer topographies." },
        { title: "Pretest", detail: "Probe imitation of the selected models before training to confirm they are not already imitated." },
        { title: "Sequence and train", detail: "Present models with prompts, reinforce imitation, fade prompts, mix trained and untrained models to probe generalization." },
        { title: "Terminate with criteria", detail: "End when the learner imitates the first presentation of five consecutive novel models across three sessions." },
      ],
    },
    {
      title: "Design a video modeling program",
      intro: "Use video to deliver consistent models.",
      steps: [
        { title: "Pick a target", detail: "Choose a discrete skill or short chain." },
        { title: "Decide model type", detail: "Live, peer, or self model; same age and characteristics aid effectiveness." },
        { title: "Produce video", detail: "Short clip showing the model performing the target in an authentic context with emphasis on critical components." },
        { title: "Present and probe", detail: "Show the video, ask the learner to perform, reinforce; probe across novel exemplars." },
        { title: "Fade", detail: "Reduce the number of model viewings; check for maintenance and generalization." },
      ],
    },
  ],
  examples: [
    { scenario: "Teacher demonstrates clapping; the learner immediately claps the same way and is praised; clapping after the demonstration increases.",
      application: "Imitation: model occasions a formally similar response closely in time, with the model as the controlling variable.",
      sourceHint: "Imitation entails four criteria." },
    { scenario: "A child sees a peer get praise for sharing a toy and then begins sharing more often without being trained.",
      application: "Observational learning: the child uses both the model's behavior AND its consequences.",
      sourceHint: "Observational learning entails another person's behavior and its consequence(s)." },
    { scenario: "A video shows a learner brushing teeth correctly; the learner watches a 30-second clip of themselves doing it and then performs the chain in the bathroom.",
      application: "Video self-modeling.",
      sourceHint: "In video self-modeling, the learners observe a video of themselves successfully performing the target behavior and imitates their self-model." },
  ],
  comparisons: [
    {
      title: "Imitation vs Observational learning",
      left:  { label: "Imitation", points: [
        "Four criteria including formal similarity and brief latency.",
        "Does not require knowing the model's consequence.",
      ]},
      right: { label: "Observational learning", points: [
        "Includes the model's behavior AND consequence.",
        "May or may not produce imitation.",
        "Requires attending, imitating, and discriminating.",
      ]},
    },
    {
      title: "Live modeling vs Video modeling",
      left:  { label: "Live modeling", points: [
        "Immediate and customizable.",
        "Quality varies session to session.",
      ]},
      right: { label: "Video modeling / self-modeling", points: [
        "Consistent presentation across sessions.",
        "Video self-modeling shows the learner succeeding.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Imitation is just any behavior that looks like someone else's.",
      reality: "True imitation requires all four criteria (occasioned by a model, formal similarity, brief latency, model as controlling variable).",
      sourceHint: "Imitation entails four criteria.",
    },
    {
      myth: "Observational learning requires the observer to imitate the modeled behavior.",
      reality: "Observational learning may produce imitation but does not require it; the observer can use the model's consequences to predict their own without acting.",
      sourceHint: "Observational learning typically involves imitation but does not require it.",
    },
    {
      myth: "Modeling alone is enough.",
      reality: "Effective modeling typically includes rehearsal, feedback, instructions, and observed reinforcement for the target.",
      sourceHint: "The effectiveness of modeling may be enhanced by... rehearsal and feedback... the learner observing the model receiving reinforcement.",
    },
    {
      myth: "Delayed imitation is a basic form of imitation.",
      reality: "Most delayed imitation is better understood by other behavioral principles and processes.",
      sourceHint: "Most instances of delayed imitation may be better understood with other behavioral principles and processes.",
    },
  ],
  examChecklist: [
    "State the four criteria of imitation.",
    "Define a model and formal similarity.",
    "Define generalized imitation.",
    "List the five steps of Striefel's imitation training protocol.",
    "List four guidelines for imitation training.",
    "Distinguish video modeling from video self-modeling.",
    "List four factors that enhance modeling effectiveness.",
    "Define observational learning and list its three prerequisite skills.",
  ],
  flashcardTerms: [
    "Imitation", "Model", "Generalized imitation", "Imitation training",
    "Modeling", "Video modeling", "Video self-modeling", "Observational learning",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which is NOT one of the four criteria of imitation?",
      options: [
        { label: "A", text: "Behavior is occasioned by another person's model" },
        { label: "B", text: "Formal similarity with the model" },
        { label: "C", text: "Imitation must be on a continuous reinforcement schedule" },
        { label: "D", text: "Model is the primary controlling variable" },
      ],
      answer: "C",
      explanation: "The four criteria are model-occasioned, formal similarity, brief latency, and model as primary controlling variable.",
      topic: "Imitation criteria",
      sourceHint: "Imitation entails four criteria.",
    },
    {
      id: "Q2",
      question: "Generalized imitation refers to:",
      options: [
        { label: "A", text: "A single imitation under strict stimulus control" },
        { label: "B", text: "Imitating a wide variety of unprompted, untrained, non-reinforced models across settings" },
        { label: "C", text: "Imitation that does not require the model to be present" },
        { label: "D", text: "Symbolic imitation only" },
      ],
      answer: "B",
      explanation: "Generalized imitation is broad imitation of novel, untrained, unprompted, non-reinforced models.",
      topic: "Generalized imitation",
      sourceHint: "Generalized imitation refers to a learner imitating a wide variety of unprompted, untrained, non-reinforced modeled behaviors in different settings and situations.",
    },
    {
      id: "Q3",
      question: "Which is a PREREQUISITE for observational learning?",
      options: [
        { label: "A", text: "Imitation but not attending" },
        { label: "B", text: "Discriminating but not imitating" },
        { label: "C", text: "Attending, imitating, AND discriminating" },
        { label: "D", text: "Generalized imitation only" },
      ],
      answer: "C",
      explanation: "Observational learning requires all three skills; if any is lacking it must be taught separately.",
      topic: "Observational learning prerequisites",
      sourceHint: "Three requisite skills are central to developing an observational learning repertoire: attending, imitating, and discriminating.",
    },
    {
      id: "Q4",
      question: "Video self-modeling involves:",
      options: [
        { label: "A", text: "A peer demonstrating the target on video" },
        { label: "B", text: "The learner watching a video of themselves performing the target successfully" },
        { label: "C", text: "A live model in the same room" },
        { label: "D", text: "Reinforcing the model" },
      ],
      answer: "B",
      explanation: "Video self-modeling uses footage of the learner succeeding and then prompts the learner to imitate that self-model.",
      topic: "Video self-modeling",
      sourceHint: "In video self-modeling, the learners observe a video of themselves successfully performing the target behavior and imitates their self-model.",
    },
  ],
  reviewPrompts: [
    "Identify the four imitation criteria in a real example.",
    "Sketch a Striefel-style imitation training plan for a non-imitative learner.",
    "List five factors you would build into a modeling program to enhance effectiveness.",
    "Distinguish observational learning from imitation using one example each.",
  ],
  recap: [
    "Imitation has four criteria (model-occasioned, formal similarity, brief latency, model as controlling variable). Generalized imitation is broad, untrained, unprompted, non-reinforced imitation across models.",
    "Modeling (live, video, self) accelerates skill acquisition. Effectiveness rises with similar/prestigious models, emphasis on critical aspects, authentic context, rehearsal, feedback, and observed reinforcement.",
    "Observational learning extends imitation by using both behavior AND consequences. It requires attending, imitating, and discriminating; missing skills must be taught separately.",
  ],
  sourceExcerpts: [
    {
      heading: "Four criteria",
      text: "Imitation entails four criteria: (a) The imitative behavior is occasioned by another person's model of the behavior (or symbolic representation thereof), (b) the imitative behavior has formal similarity with the model, (c) the imitative behavior follows the modeled behavior closely in time, and (d) the model is the primary controlling variable for the imitative behavior.",
    },
    {
      heading: "Imitation training (Striefel, 1974)",
      text: "Striefel's (1974) imitation training protocol for practitioners has the following components: (a) assess, and teach if necessary, prerequisite skills for imitation training; (b) select models for training; (c) pretest; (d) sequence models for training; and (e) conduct imitation training.",
    },
    {
      heading: "Observational learning prerequisites",
      text: "Three requisite skills are central to developing an observational learning repertoire: attending, imitating, and discriminating. If any of the skills are lacking, they can be taught separately and then combined.",
    },
  ],
};

export default chapter21Module;
